/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { PreferredUnitType } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PreferredUnitProps {
  id: string;
  unitAbbreviation: string;
  rank: number;
  type: PreferredUnitType;
}

export class PreferredUnitRecord extends Record<PreferredUnitProps>({
  id: null as any,
  unitAbbreviation: null as any,
  rank: null as any,
  type: null as any,
}) {
  public static fromJS(json: {
    id: string;
    unitAbbreviation: string;
    rank: number;
    type: PreferredUnitType;
  }): PreferredUnitRecord {
    if (json == null) {
      throw new Error('PreferredUnitRecord.fromJS: parameter cannot be null');
    }
    return new PreferredUnitRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PreferredUnitJson.id',
      ),
      unitAbbreviation: getFromOrThrow(
        json,
        (j) => j.unitAbbreviation,
        'PreferredUnitJson.unitAbbreviation',
      ),
      rank: getFromOrThrow(json, (j) => propValueOrDefault(j, 'rank', 0), 'PreferredUnitJson.rank'),
      type: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'type', PreferredUnitType.Unit),
        'PreferredUnitJson.type',
      ),
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    unitAbbreviation: string;
    rank: number;
    type: PreferredUnitType;
  }): [PreferredUnitRecord, Error[]] {
    if (json == null) {
      throw new Error('PreferredUnitRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PreferredUnitRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PreferredUnitJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitAbbreviation: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.unitAbbreviation, 'PreferredUnitJson.unitAbbreviation'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      rank: tryOrDefault(
        () =>
          getFromOrThrow(json, (j) => propValueOrDefault(j, 'rank', 0), 'PreferredUnitJson.rank'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      type: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'type', PreferredUnitType.Unit),
            'PreferredUnitJson.type',
          ),
        (err) => errors.push(err),
      )!,
    });

    return [record, errors];
  }

  constructor(props: PreferredUnitProps) {
    super(props);
    if (props == null) {
      throw new Error('PreferredUnitRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PreferredUnitViewModelBase implements PreferredUnitProps {
  protected readonly _props: PreferredUnitProps;
  private _json?: PreferredUnitProps;

  get id(): string {
    return this._props.id;
  }
  get unitAbbreviation(): string {
    return this._props.unitAbbreviation;
  }
  get rank(): number {
    return this._props.rank;
  }
  get type(): PreferredUnitType {
    return this._props.type;
  }

  public get isAgencyLocation() {
    return this._props.type === PreferredUnitType.AgencyLocation;
  }
  public get isUnit() {
    return this._props.type === PreferredUnitType.Unit;
  }

  public isAnyType(...preferredUnitType: PreferredUnitType[]) {
    return preferredUnitType.includes(this._props.type);
  }

  constructor(props: PreferredUnitProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        unitAbbreviation: this._props.unitAbbreviation,
        rank: this._props.rank,
        type: this._props.type,
      };
    }
    return this._json;
  }
}
